<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import PlanosResumoAPI from 'dashboard/api/planosResumo';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const { t } = useI18n();

const resumo = ref(null);
const carregando = ref(false);
const erro = ref(false);

const carregar = async () => {
  carregando.value = true;
  erro.value = false;
  try {
    const response = await PlanosResumoAPI.get();
    resumo.value = response.data?.resumo || null;
  } catch {
    resumo.value = null;
    erro.value = true;
  } finally {
    carregando.value = false;
  }
};

onMounted(carregar);

const semPlano = computed(() => !resumo.value?.plano_atual);
const hoje = computed(() => resumo.value?.hoje || null);
const uso = computed(() => resumo.value?.uso || null);
const proximo = computed(() => resumo.value?.proximo || null);

// O servico manda reais inteiros; sem formatar, a tela mostrava "497" solto.
const moedaFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const moeda = valor =>
  valor === null || valor === undefined ? '' : moedaFormatter.format(valor);

// A chave interna (lead_scoring) vira o mesmo nome da tela de Modulos.
const nomeModulo = chave =>
  t(`GENERAL_SETTINGS.FORM.MODULES.ITEMS.${chave}.LABEL`);

const usoLinha = (usado, incluso) => {
  if (incluso === null || incluso === undefined) return String(usado);
  return `${usado} ${t('PLANOS.PAGE.OF')} ${incluso} ${t('PLANOS.PAGE.INCLUDED')}`;
};
</script>

<template>
  <div
    class="flex flex-col w-full h-full m-0 pb-8 pt-4 px-6 overflow-auto bg-n-surface-1"
  >
    <div class="flex flex-col w-full max-w-5xl mx-auto gap-6">
      <header class="flex flex-col gap-1.5">
        <h1 class="text-heading-1 text-n-slate-12">
          {{ t('PLANOS.PAGE.TITLE') }}
        </h1>
        <p class="mb-0 max-w-3xl text-body-main text-n-slate-11">
          {{ t('PLANOS.PAGE.SUBTITLE') }}
        </p>
      </header>

      <div
        v-if="carregando"
        class="flex items-center justify-center py-20 text-n-slate-11"
      >
        {{ t('PLANOS.PAGE.LOADING') }}
      </div>

      <div
        v-else-if="erro"
        class="rounded-xl border border-n-weak bg-n-solid-2 p-5 flex flex-col gap-3"
      >
        <p class="mb-0 text-n-slate-12">{{ t('PLANOS.PAGE.ERROR') }}</p>
        <button
          type="button"
          class="inline-flex items-center justify-center min-h-11 px-4 rounded-lg bg-n-brand text-white text-sm font-medium w-fit"
          @click="carregar"
        >
          {{ t('PLANOS.PAGE.RETRY') }}
        </button>
      </div>

      <div
        v-else-if="semPlano"
        class="rounded-xl border border-n-weak bg-n-solid-2 p-5 flex flex-col gap-2"
      >
        <h2 class="text-lg font-semibold text-n-slate-12 m-0">
          {{ t('PLANOS.PAGE.EMPTY_TITLE') }}
        </h2>
        <p class="mb-0 text-n-slate-11">{{ t('PLANOS.PAGE.EMPTY_BODY') }}</p>
      </div>

      <template v-else>
        <section
          class="rounded-xl border border-n-weak bg-n-solid-2 p-5 flex flex-col gap-5"
        >
          <div class="flex items-start justify-between gap-4">
            <div>
              <p class="mb-1 text-sm text-n-slate-11">
                {{ t('PLANOS.PAGE.CURRENT') }}
              </p>
              <h2 class="text-2xl font-semibold text-n-slate-12 m-0">
                {{ resumo.plano_atual }}
              </h2>
            </div>
            <Icon icon="i-ri-stack-line" class="size-6 text-n-slate-10" />
          </div>

          <dl class="grid gap-4 sm:grid-cols-2">
            <div>
              <dt class="text-sm text-n-slate-11">
                {{ t('PLANOS.PAGE.TODAY') }}
              </dt>
              <dd
                v-if="hoje"
                class="m-0 text-3xl font-semibold tabular-nums text-n-slate-12"
              >
                {{ moeda(hoje.total) }}
              </dd>
              <dd v-else class="m-0 text-base text-n-slate-12">
                {{ t('PLANOS.PAGE.NO_PRICE') }}
              </dd>
            </div>
            <div v-if="hoje" class="grid grid-cols-2 gap-3">
              <div>
                <dt class="text-sm text-n-slate-11">
                  {{ t('PLANOS.PAGE.PLAN') }}
                </dt>
                <dd class="m-0 text-lg tabular-nums text-n-slate-12">
                  {{ moeda(hoje.plano) }}
                </dd>
              </div>
              <div>
                <dt class="text-sm text-n-slate-11">
                  {{ t('PLANOS.PAGE.EXTRAS') }}
                </dt>
                <dd class="m-0 text-lg tabular-nums text-n-slate-12">
                  {{ moeda(hoje.extras) }}
                </dd>
              </div>
              <div>
                <dt class="text-sm text-n-slate-11">
                  {{ t('PLANOS.PAGE.EXTRAS_AGENTS') }}
                </dt>
                <dd class="m-0 text-sm tabular-nums text-n-slate-11">
                  {{ moeda(hoje.extras_atendentes) }}
                </dd>
              </div>
              <div>
                <dt class="text-sm text-n-slate-11">
                  {{ t('PLANOS.PAGE.EXTRAS_INBOXES') }}
                </dt>
                <dd class="m-0 text-sm tabular-nums text-n-slate-11">
                  {{ moeda(hoje.extras_caixas) }}
                </dd>
              </div>
            </div>
          </dl>
        </section>

        <section
          v-if="uso"
          class="rounded-xl border border-n-weak bg-n-solid-2 p-5 flex flex-col gap-4"
        >
          <h2 class="text-lg font-semibold text-n-slate-12 m-0">
            {{ t('PLANOS.PAGE.USAGE') }}
          </h2>
          <dl class="grid gap-4 sm:grid-cols-2">
            <div>
              <dt class="text-sm text-n-slate-11">
                {{ t('PLANOS.PAGE.AGENTS') }}
              </dt>
              <dd class="m-0 text-lg tabular-nums text-n-slate-12">
                {{ usoLinha(uso.atendentes, uso.atendentes_inclusos) }}
              </dd>
            </div>
            <div>
              <dt class="text-sm text-n-slate-11">
                {{ t('PLANOS.PAGE.INBOXES') }}
              </dt>
              <dd class="m-0 text-lg tabular-nums text-n-slate-12">
                {{ usoLinha(uso.caixas, uso.caixas_inclusos) }}
              </dd>
            </div>
          </dl>
        </section>

        <section
          v-if="proximo"
          class="rounded-xl border border-n-weak bg-n-solid-2 p-5 flex flex-col gap-4"
        >
          <div>
            <h2 class="text-lg font-semibold text-n-slate-12 m-0">
              {{ t('PLANOS.PAGE.NEXT_TITLE') }}
            </h2>
            <p class="mt-1 mb-0 text-sm text-n-slate-11">
              {{ t('PLANOS.PAGE.NEXT_BODY') }}
            </p>
          </div>
          <p class="mb-0 text-2xl font-semibold text-n-slate-12">
            {{ proximo.nome }}
          </p>
          <dl class="grid gap-4 sm:grid-cols-2">
            <div>
              <dt class="text-sm text-n-slate-11">
                {{ t('PLANOS.PAGE.NEXT_COST') }}
              </dt>
              <dd
                v-if="proximo.hoje"
                class="m-0 text-2xl font-semibold tabular-nums text-n-slate-12"
              >
                {{ moeda(proximo.hoje.total) }}
              </dd>
            </div>
            <div>
              <dt class="text-sm text-n-slate-11">
                {{ t('PLANOS.PAGE.NEXT_INCLUDES') }}
              </dt>
              <dd class="m-0 text-base tabular-nums text-n-slate-12">
                {{ proximo.incluso_atendentes }}
                {{ t('PLANOS.PAGE.AGENTS') }},
                {{ proximo.incluso_caixas }}
                {{ t('PLANOS.PAGE.INBOXES') }}
              </dd>
            </div>
          </dl>
          <div v-if="proximo.modulos && proximo.modulos.length">
            <p class="mb-2 text-sm text-n-slate-11">
              {{ t('PLANOS.PAGE.NEXT_MODULES') }}
            </p>
            <ul class="m-0 p-0 list-none flex flex-col gap-1">
              <li
                v-for="modulo in proximo.modulos"
                :key="modulo.chave"
                class="flex items-center justify-between text-sm text-n-slate-12"
              >
                <span>{{ nomeModulo(modulo.chave) }}</span>
                <span class="tabular-nums">{{ moeda(modulo.preco) }}</span>
              </li>
            </ul>
          </div>
        </section>
      </template>
    </div>
  </div>
</template>
